import { Box, Typography } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { EyebrowLabel } from "@/ui/molecules/EyebrowLabel";
import { JobSelector } from "@/ui/molecules/JobSelector";
import { RoleChips } from "@/ui/molecules/RoleChips";
import { type Job, NurseFullRoleShort, NurseRole } from "@/utils/models";
import { type LocationEditorDesign, smallLabelTypographySx, V1_JOB_SELECTOR_WIDTH } from "./consts";
import type { LocationConfigSlimData } from "./locationConfigData";

type JobOrRolesFieldCommonProps = {
	/** With job management on, the location is staffed by one job; otherwise by its roles. */
	jobManagementEnabled: boolean;
	jobs: Job[];
	selectedJobId: string;
	onJobChange: (jobId: string) => void;
	roles: LocationConfigSlimData["roles"];
	onRolesChange: (roles: (NurseRole | "all")[]) => void;
	onDropdownOpen: () => void;
	onDropdownClose: () => void;
};

/** v2 alone has a read-only state: the Locations page's editor under a Schedule Template edit lock. */
export type JobOrRolesFieldProps = JobOrRolesFieldCommonProps &
	({ design: Extract<LocationEditorDesign, "v1"> } | { design: Extract<LocationEditorDesign, "v2">; disabled: boolean });

/** Who staffs the location: a single job, or a set of roles when job management is off. */
export const JobOrRolesField: FC<JobOrRolesFieldProps> = (props): ReactElement => {
	const { jobManagementEnabled, jobs, selectedJobId, onJobChange, roles, onRolesChange, onDropdownOpen, onDropdownClose } = props;
	const { t } = useTranslation();
	const selectedRoles = roles.map((role) => (role === "all" ? "all" : NurseRole[NurseFullRoleShort[role]]));
	switch (props.design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.5,
					}}
				>
					{jobManagementEnabled ? (
						<>
							<Typography sx={smallLabelTypographySx}>{t("schedule.template.v3.editor.chooseJob")}</Typography>
							<JobSelector
								variant="dropdown"
								jobs={jobs}
								selectedJobIds={selectedJobId ? [selectedJobId] : []}
								// DEFAULT: clearing the single-select leaves no job; "" is the form's "no job" (moved verbatim).
								onSelectedJobsChanged={(selected) => onJobChange(selected[0] ?? "")}
								hideAllOption
								singleSelect
								onOpen={onDropdownOpen}
								onClose={onDropdownClose}
								formControlSx={{ width: V1_JOB_SELECTOR_WIDTH }}
							/>
						</>
					) : (
						<>
							<Typography sx={smallLabelTypographySx}>{t("schedule.template.v3.editor.chooseRoles")}</Typography>
							<RoleChips onSelectedRoleFiltersChanged={onRolesChange} initiallySelected={selectedRoles} />
						</>
					)}
				</Box>
			);
		case "v2":
			// The job is a field in the form's row of fields (its label sits in the outline, as the other fields'
			// do); roles are a chip set, so they take a small heading above them instead. Their labels are the
			// Locations page's own terms, the ones its filters, cards and duplicate notices use.
			return jobManagementEnabled ? (
				<JobSelector
					variant="dropdown"
					label={t("locations.grid.col.job")}
					jobs={jobs}
					selectedJobIds={selectedJobId ? [selectedJobId] : []}
					// DEFAULT: clearing the single-select leaves no job; "" is the form's "no job" (as v1 reads it).
					onSelectedJobsChanged={(selected) => onJobChange(selected[0] ?? "")}
					hideAllOption
					singleSelect
					fullWidth
					onOpen={onDropdownOpen}
					onClose={onDropdownClose}
					disabled={props.disabled}
				/>
			) : (
				<Box role="group" aria-label={t("locations.grid.col.roles")} sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
					<EyebrowLabel ariaHidden>{t("locations.grid.col.roles")}</EyebrowLabel>
					<RoleChips onSelectedRoleFiltersChanged={onRolesChange} initiallySelected={selectedRoles} disabled={props.disabled} />
				</Box>
			);
	}
};
